import * as React from "react";

import {
  Button,
  ButtonGroup,
  Grid,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";

import PersonIcon from "@mui/icons-material/Person";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  Book,
  Bookmark,
  BookmarkBorder,
  ContentCopy,
  ContentPaste,
  PlayCircleOutline,
} from "@mui/icons-material";
import NotesIcon from "@mui/icons-material/Notes";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import InsightsIcon from "@mui/icons-material/Insights";

import { ReactComponent as ChatbotSVGBase } from "../../assets/chatbot.svg";
import hyperscaler_mapping from "../../data/modelMapping";

import { GET_TABLE, SAVE_USER_PROMPT } from "../../endpoint";

import { useAppDispatch, useAppSelector } from "../../hooks/reduxhook";
import { addshowSources } from "../../redux/slices/showSourcesSlice";
import apiCall from "../../utils/apiCall";
import { addPrompts, getType } from "../../redux/slices/typeSlice";
import { useMsal } from "@azure/msal-react";
import TableeChatDisplay from "../TableChatDisplay";

import hyperScalerMapping from "../../data/hyperscalerMapping";
import { COLLECTION, NEO4J, PDF } from "../../data/constants";
import ExcerptLink from "./ExcerptLink";
import { NeoGraph } from "../Neograph";
import { getCypherDetails } from "../../redux/slices/conversationSlice";
import Markdown from "../Markdown";

import security_svg from "../../assets/security_secondary.svg";
import { addError } from "../../redux/slices/errorSlice";
import { getModelDetails } from "../../redux/slices/modelSlice";
import { useTheme } from "@mui/material";

const ModelImage = styled("img")({
  margin: "auto",
  width: "20px",
  height: "20px",
  objectFit: "contain",
});

const ChatbotSVG = styled(ChatbotSVGBase)(({ theme }) => ({
  margin: "auto",
  width: "20px",
  color: theme.palette.mode === "dark" ? "#aaa" : "#333",
}));

interface I_Sources {
  page?: string;
  source?: string;
  text?: string;
  time_stamp?: string;
  title?: string;
  publish_date?: string;
  author?: string;
  url?: string;
  excerpt?: string;
}

interface I_DbCreds {
  username: string;
  password: string;
  url: string;
}
interface IChatMessageProps {
  who: string;
  message:
    | {
        isError?: boolean;
        color?: string;
        detail?: string;
        result?: string;
      }
    | string;
  sources: I_Sources[];
  model?: string;
  chat_type?: string;
  cypher: string;
  org_cypher: string;
  db_creds: I_DbCreds;
  image_model?: string;
  collection_chunks?: string[];
  collection_images?: string[];
  pii?: boolean;
}

const ChatMessage: React.FunctionComponent<IChatMessageProps> = (props) => {
  const dispatch = useAppDispatch();
  const [copy, setCopy] = React.useState(false);
  const [bookmark, setBookmark] = React.useState(false);
  const [tableView, setTableView] = React.useState(false);
  const [tableData, setTableData] = React.useState([]);
  const [tableError, settableError] = React.useState("");
  const [showMore, setShowMore] = React.useState(false);
  const [cypher, setCypher] = React.useState(0);

  const chatType = useAppSelector(getType);
  const modelsmapping = useAppSelector(getModelDetails)?.modelmaps;
  const cypherDetails = useAppSelector(getCypherDetails);
  const modelDetails = useAppSelector(getModelDetails);

  const { accounts } = useMsal();
  const account = accounts[0];
  const formatModelName = (rawName: string): string => {
    if (!rawName) return "";

    return (
      rawName
        // Remove team / environment suffixes
        .replace(/_AI_Team|AI_Team|_team|team/gi, "")

        // Normalize separators to spaces
        .replace(/[_-]+/g, " ")

        // Normalize GPT versions (gpt 4.0 → GPT 4.0)
        .replace(/\bgpt\b/gi, "GPT")

        // Normalize Gemini
        .replace(/\bgemini\b/gi, "Gemini")

        // Collapse multiple spaces
        .replace(/\s+/g, " ")

        // Trim
        .trim()
    );
  };
  const parseMessage = (
    message:
      | {
          isError?: boolean;
          color?: string;
          detail?: string;
          result?: string;
          model?: string;
        }
      | string,
  ) => {
    // ✅ Case 1: Already an object → return directly
    if (typeof message === "object" && message !== null) {
      return message;
    }

    // ✅ Case 2: String response
    if (typeof message === "string") {
      /**
       * Extract ONLY the value of 'result'
       * WITHOUT modifying the content
       */
      const match = message.match(
        /['"]result['"]\s*:\s*['"]([\s\S]*?)['"]\s*(,|\})/,
      );

      if (match) {
        return {
          result: match[1], // 👈 AS‑IS, untouched
        };
      }

      // ✅ Fallback: treat string as plain text
      return { result: message };
    }

    return { result: "" };
  };
  const getMessage = () => {
    const parsed = parseMessage(props.message);

    if (parsed.isError) {
      if (parsed.detail === "Internal Server Error") {
        return "Error Occurred. Please try after sometime.!!!";
      }
      return `Error Occurred. Details: ${parsed.detail}`;
    }

    return parsed.result ?? parsed.detail ?? "";
  };

  const savePrompt = async () => {
    apiCall("POST", SAVE_USER_PROMPT, {
      prompt: getMessage(),
      user_email: account.username,
      chat_type: chatType.prompt_storage,
    })
      .then((res) => {
        dispatch(addPrompts(res.result));
        setBookmark(true);
      })
      .catch((err: any) => {
        dispatch(addError("Failed to save the user query.Please try again."));
      });
  };

  const seeTableView = () => {
    if (tableView) {
      setTableView(false);
    } else if (tableData.length === 0)
      apiCall("POST", GET_TABLE, {
        query: getMessage(),
        user_email: account.username,
      }).then((res: any) => {
        if (typeof res.result === "string") settableError(res.result);
        else {
          settableError("");
          setTableView(true);
          setTableData(res.result);
        }
      });
    else {
      setTableView(true);
    }
  };

  const textFormatter = (content: string) => {
    const splitArray = content.split(/\[Excerpt (\d+)\]/);
    return splitArray.map((part, index) => {
      if (isNaN(parseFloat(part))) {
        return <React.Fragment key={index}> {part}</React.Fragment>;
      } else {
        const data = props.sources.filter(
          (k) => k.excerpt?.toLowerCase() === "excerpt " + Number(part),
        );
        return (
          <ExcerptLink
            key={index}
            text={data[0].time_stamp ?? "hello"}
            index={index}
            {...data[0]}
          />
        );
      }
    });
  };

  const getModelKey = (modelName?: string) =>
    modelName
      ? modelDetails?.models?.chat
          ?.find((m) => m.model_name === modelName)
          ?.model.split("/")[0] || null
      : null;

  const theme = useTheme();
  return (
    <Grid
      container
      my={2}
      sx={{
        position: "relative",
      }}
    >
      <Grid
        container
        item
        sx={{
          background: theme.palette.mode === "dark" ? "#111" : "#fff",
          borderRadius: "50%",
          width: "35px",
          height: "35px",
          marginRight: "15px",
        }}
        justifyContent="center"
        alignItems="center"
      >
        {props.who === "gpt" ? (
          (() => {
            const modelKey = getModelKey(props.model);
            if (modelKey && hyperscaler_mapping[modelKey]) {
              return (
                <ModelImage
                  src={hyperscaler_mapping[modelKey]}
                  alt={props.model || "Default Bot"}
                />
              );
            } else {
              return <ChatbotSVG />;
            }
          })()
        ) : (
          <PersonIcon
            sx={{
              fontSize: "15px",
            }}
            color="primary"
          />
        )}
      </Grid>
      <Grid
        item
        xs
        sx={{
          background: theme.palette.mode === "dark" ? "#111" : "#fff",
          borderRadius: "5px",
          padding: "10px 20px",
          marginRight: "10px",
        }}
      >
        {props.who === "gpt" && props.chat_type !== "video" ? (
          <>
            {tableView ? (
              tableError !== "" ? (
                <Typography>{tableError}</Typography>
              ) : (
                <TableeChatDisplay
                  rows={tableData?.map((t: object, i) => ({ ...t, id: i }))}
                  columns={[
                    ...Object.keys(tableData[0]).map((r) => ({
                      field: `${r}`,
                      headerName: `${r}`,
                      width: `${r.length * 20}`,
                      type: "number",
                    })),
                  ]}
                  pageSizeOptions={[5, 10, 25]}
                />
              )
            ) : cypher === 0 ? (
              <>
                {/* {props.message.split("\n").map((e, i) => (
                  <Typography
                    key={i}
                    sx={{
                      mb:
                        props.who === "gpt" &&
                        i + 1 === props.message.split("\n").length
                          ? 0
                          : 2,
                    }}
                  >
                    {e}
                  </Typography>
                ))} */}
                <Grid container>
                  <div className="break-word-container">
                    <Markdown
                      markdownText={getMessage()}
                      isError={
                        typeof props.message === "object" &&
                        props.message.isError
                      }
                    />
                  </div>
                </Grid>
              </>
            ) : cypher === 1 ? (
              <Typography
                sx={{
                  mb: 2,
                }}
              >
                <i>{props.org_cypher}</i>
              </Typography>
            ) : (
              <div data-testid="NeoGraph">
                <NeoGraph
                  width={"100%"}
                  height={`200`}
                  containerId={"id1"}
                  neo4jUri={props?.db_creds?.url?.replace("+ssc", "") ?? ""}
                  neo4jUser={props?.db_creds?.username ?? ""}
                  neo4jPassword={props?.db_creds?.password ?? ""}
                  backgroundColor={"#fff"}
                  cypher={props.cypher}
                  alertWidth="97.5%"
                />
              </div>
            )}
          </>
        ) : props.who === "gpt" && props.chat_type === "video" ? (
          <Typography>{textFormatter(getMessage())}</Typography>
        ) : (
          <Grid container>
            <Typography
              color="primary"
              sx={{
                flex: 1,
                width: "50%",
                flexWrap: "wrap",
                overflowWrap: "break-word",
              }}
            >
              {getMessage()
                .split("\n")
                .map((m: string, i: number) => (
                  <span key={i}>
                    {m}
                    <br />
                  </span>
                ))}
            </Typography>
            <Grid>
              <Tooltip title="Copy to clipboard">
                {copy ? (
                  <ContentPaste fontSize="small" color="primary" />
                ) : (
                  <Tooltip title="copy">
                    <ContentCopy
                      fontSize="small"
                      color="primary"
                      sx={{
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        navigator.clipboard.writeText(getMessage());
                        setCopy(true);
                        setTimeout(() => {
                          setCopy(false);
                        }, 2000);
                      }}
                      aria-label="copy icon"
                    />
                  </Tooltip>
                )}
              </Tooltip>
              <Tooltip title="Save as query">
                {bookmark ? (
                  <Bookmark
                    fontSize="small"
                    color="secondary"
                    aria-label="book-mark"
                  />
                ) : (
                  <BookmarkBorder
                    fontSize="small"
                    color="primary"
                    sx={{
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      savePrompt();
                      setBookmark(true);
                      setTimeout(() => {
                        setBookmark(false);
                      }, 2000);
                    }}
                    aria-label="book-mark-border"
                  />
                )}
              </Tooltip>
            </Grid>
          </Grid>
        )}
        {props.sources.length > 0 &&
          (props.chat_type === PDF || props.chat_type === "rag") && (
            <Grid container alignItems="center" my={1.5}>
              <Book
                color="primary"
                sx={{
                  fontSize: "14px",
                }}
              />
              <Typography color="primary">Sources: &nbsp;</Typography>
              {props.sources.map(
                (source, i) =>
                  (i < 5 || showMore) && (
                    <Typography
                      key={i}
                      onClick={() =>
                        dispatch(
                          addshowSources({
                            index: i,
                            sources: props.sources,
                          }),
                        )
                      }
                      sx={{
                        cursor: "pointer",
                      }}
                    >
                      {Number(source.page) >= 0 && <>{source.page} of </>}
                      {source?.source?.split("/")[2].split("---")[0]}.
                      {source?.source?.split("/")[2].split(".")[2]},&nbsp;&nbsp;
                    </Typography>
                  ),
              )}
            </Grid>
          )}
        {props.sources.length > 4 &&
          (props.chat_type === PDF || props.chat_type === "rag") && (
            <Button
              variant="text"
              sx={{
                width: "160px",
                fontSize: "10px",
                marginLeft: "auto",
                marginTop: "-10px",
                display: "block",
              }}
              size="small"
              color="primary"
              onClick={() => setShowMore((prevShowMore) => !prevShowMore)}
            >
              {showMore ? (
                <>
                  Show less sources
                  <ExpandLessIcon
                    sx={{
                      fontSize: "14px",
                      position: "relative",
                      top: "5px",
                    }}
                  />
                </>
              ) : (
                <>
                  Show more sources
                  <ExpandMoreIcon
                    sx={{
                      fontSize: "14px",
                      position: "relative",
                      top: "5px",
                    }}
                  />
                </>
              )}
            </Button>
          )}
        {props.image_model && (
          <>
            <Grid
              container
              direction="column"
              my={2}
              sx={{
                borderTop: "1px solid rgba(0,0,0,0.1)",
                pt: 1,
              }}
            >
              {props.collection_chunks !== undefined &&
                props.collection_chunks?.length > 0 && (
                  <Grid container>
                    <img
                      src={hyperScalerMapping[`rag`]?.icon}
                      alt={hyperScalerMapping[`rag`]?.displayName}
                      width="20px"
                    />
                    <Typography fontSize="small">
                      Sources:&nbsp;
                      {props.collection_chunks?.map((chunk, i) => {
                        const names = chunk.split("---");
                        return (
                          <span className="chunk" key={i}>
                            {names[0]}.{names[names.length - 1]},
                          </span>
                        );
                      })}
                    </Typography>
                  </Grid>
                )}{" "}
              {props.collection_images !== undefined &&
                props.collection_images?.length > 0 && (
                  <Grid container mt={1}>
                    <img
                      src={hyperScalerMapping[`image`]?.icon}
                      alt={hyperScalerMapping[`image`]?.displayName}
                      width="20px"
                    />
                    <Typography fontSize="small">
                      Sources:&nbsp;
                      {props.collection_images?.map((chunk, i) => {
                        const names = chunk.split("---");
                        return (
                          <span key={i} className="chunk">
                            {names[0]}.{names[names.length - 1]},
                          </span>
                        );
                      })}
                    </Typography>
                  </Grid>
                )}
            </Grid>
          </>
        )}

        {/* {props.who === "gpt" && (
          
        )} */}

        {props.who === "gpt" && (
          <Grid
            container
            justifyContent="space-between"
            direction="row"
            alignItems="center"
            mt={2}
          >
            <Grid
              display="flex"
              container
              alignItems="center"
              justifyContent="center"
              flex={1}
              sx={{
                // border: "1px solid #FD5F07",
                p: 0.5,
                borderRadius: "10px",
                width: "350px",
                cursor: "pointer",
              }}
              // onClick={seeTableView}
            >
              {props.chat_type === "neo4j" && (
                <ButtonGroup>
                  <Button
                    variant="outlined"
                    startIcon={<NotesIcon fontSize="small" />}
                    size="small"
                    onClick={() => setCypher(0)}
                  >
                    Text
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AcUnitIcon fontSize="small" />}
                    size="small"
                    onClick={() => setCypher(1)}
                  >
                    Cypher
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<InsightsIcon fontSize="small" />}
                    size="small"
                    onClick={() => setCypher(2)}
                  >
                    Graph
                  </Button>
                </ButtonGroup>
              )}
            </Grid>
            {props.model !== "" && (
              <Grid
                display="flex"
                direction="row"
                justifyContent="space-evenly"
              >
                {props.pii && (
                  <Grid
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    fontStyle="italic"
                    sx={{
                      p: 0.5,
                      mr: 2,
                    }}
                  >
                    <img src={security_svg} alt="PII" width={20} />
                    <Typography fontSize="small">PII Redacted</Typography>
                  </Grid>
                )}
                <Grid
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  fontStyle="italic"
                  sx={{
                    p: 0.5,
                    mr: 1,
                  }}
                >
                  {props.image_model && (
                    <>
                      <img
                        src={hyperScalerMapping[`rag`]?.icon}
                        alt={hyperScalerMapping[`rag`]?.displayName}
                        width="20px"
                      />
                      <span className="chunk">:</span>
                    </>
                  )}
                  {/* {props?.model && (
                    <>
                      <img
                        src={modelsmapping[`${props?.model}`]?.icon}
                        alt={modelsmapping[`${props?.model}`]?.name}
                        width="20px"
                      />
                      <ChatbotSVG />
                      &nbsp;
                      <Typography
                        color="primary"
                        fontSize={12}
                        sx={{
                          textTransform: "capitalize",
                        }}
                      >
                        {props?.model
                          .replaceAll("-", " ")
                          .replaceAll("AI_Team", "")
                          .replaceAll("iAura_team", "iAura")}
                      </Typography>
                    </>
                  )} */}
                  {props?.model && (
                    <>
                      <img
                        src={modelsmapping[`${props?.model}`]?.icon}
                        alt={modelsmapping[`${props?.model}`]?.name}
                        width="20px"
                      />
                      &nbsp;
                      <Typography color="primary" fontSize={12}>
                        {formatModelName(
                          modelsmapping[`${props?.model}`]?.name ??
                            props?.model,
                        )}
                      </Typography>
                    </>
                  )}
                </Grid>
                {props.image_model && (
                  <Grid
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    fontStyle="italic"
                    sx={{
                      p: 0.5,
                    }}
                  >
                    <img
                      src={hyperScalerMapping[`image`]?.icon}
                      alt={hyperScalerMapping[`image`]?.displayName}
                      width="20px"
                    />
                    <span className="chunk">:</span>
                    <img
                      src={modelsmapping[`${props?.image_model}`]?.icon}
                      alt={modelsmapping[`${props?.image_model}`]?.name}
                      width="20px"
                    />
                    <Typography color="primary" fontSize={12}>
                      {formatModelName(
                        modelsmapping[`${props?.image_model}`]?.name ??
                          props?.image_model,
                      )}
                    </Typography>
                  </Grid>
                )}
                <Grid
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    p: 0.5,
                    ml: 3,
                  }}
                >
                  {hyperScalerMapping[`${props.chat_type}`]?.icon && (
                    <img
                      src={hyperScalerMapping[`${props.chat_type}`]?.icon}
                      alt="Chat Type"
                      width="16px"
                      className={`${
                        theme.palette.mode === "dark" ? "dark" : "light"
                      }`}
                    />
                  )}
                  &nbsp;
                  <Typography
                    color="primary"
                    fontSize={12}
                    sx={{
                      textTransform: "capitalize",
                    }}
                  >
                    {hyperScalerMapping[`${props.chat_type}`]?.displayName}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default ChatMessage;
